import { _ as _assign, b as __awaiter, c as __generator } from './tslib.es6-c6da0ae6.js';
import { useState, useEffect, useCallback, useLayoutEffect } from 'react';
import Logger from './services/logger/index.js';
import { authStates, authStateKey } from './models/user/index.js';
import { IS_SUBSCRIBED_COOKIE_NAME, GT_USER_ID_COOKIE_NAME, GOOGLE_CLIENT_ID } from './configs/constants/index.js';
import { h as AuthFlow, S as StatusOptions, b as AuthActionOptions, P as ProviderOptions, e as ComponentsOptions, i as AnalyticsSocialItemOptions, c as SocialProviderOptions, G as GOOGLE_PLATFORM_SCRIPT_URL } from './constants-e3c79897.js';
import { b as usernameSuggestion, U as UserField, a as handleNewSocialRequest, c as handleAppleRequest, d as handleGoogleResponse, e as handleFacebookResponse, f as handleSocialSignIn, i as handleSubmit, j as emailCodeSendRequest } from './requestFactory-4dc9ec32.js';
import AnalyticsService from '@picsart/rc/services/analytics';
import { u as useAuthContext, a as authStatus } from './authContext-a01efdfb.js';
import { setCookie } from './cookie/index.js';
import { getStorage, setStorage } from './services/browserStorage/index.js';
import { addRecentLoginMethod } from './services/recentLoginMethods/index.js';
import { u as useRegistrationFormContext } from './RegistrationFormContext-20b6a976.js';
import IS_BROWSER from './isBrowser/index.js';
import useDelayedScript from './useDelayedScript/index.js';
import { g as googleButtonTheme, a as googleButtonSize, b as googleButtonLabelText, c as googleButtonShape } from './constants-8010f63c.js';

var fireSocialButtonClickAnalytics = function fireSocialButtonClickAnalytics(social) {
  AnalyticsService.sendEvent('button_click', {
    screen_name: 'registration_popup',
    item: social
  });
};

function useAuth(_a) {
  var _this = this;
  var _b = _a.flow,
    flow = _b === void 0 ? AuthFlow.Default : _b,
    triggeredFrom = _a.triggeredFrom,
    authSuccessHandler = _a.authSuccessHandler,
    onTerminatedAccount = _a.onTerminatedAccount,
    preventReCaptcha = _a.preventReCaptcha,
    isAuthCombined = _a.isAuthCombined,
    showUsernameFormOnSignIn = _a.showUsernameFormOnSignIn,
    showUsernameFormOnSignUp = _a.showUsernameFormOnSignUp;
  var _c = useState(''),
    redirectAfterEmailConfirmation = _c[0],
    setRedirectAfterEmailConfirmation = _c[1];
  useEffect(function () {
    setRedirectAfterEmailConfirmation(getStorage('redirectAfterEmailConfirmation', false) || '');
  }, []);
  var _d = useAuthContext(),
    onUserChange = _d.onUserChange,
    onTokenChange = _d.onTokenChange,
    onStatusChange = _d.onStatusChange,
    tokenDataRef = _d.tokenDataRef;
  var _e = useRegistrationFormContext(),
    setCurrentPage = _e.setCurrentPage,
    setSocialData = _e.setSocialData,
    checkedStates = _e.checkedStates,
    stagedUserInfo = _e.stagedUserInfo,
    setStagedUserInfo = _e.setStagedUserInfo,
    setUsername = _e.setUsername;
  var _f = useState(null),
    terminatedAccount = _f[0],
    setTerminatedAccount = _f[1];
  var _g = useState({
      type: StatusOptions.Default,
      message: '',
      reason: ''
    }),
    serverStatus = _g[0],
    setServerStatus = _g[1];
  var handleTerminatedAccount = useCallback(function (data) {
    onTerminatedAccount === null || onTerminatedAccount === void 0 ? void 0 : onTerminatedAccount();
    setTerminatedAccount(data || null);
  }, [onTerminatedAccount]);
  var finalizeAuth = useCallback(function (provider, user) {
    var _a, _b, _c;
    if ((_a = user === null || user === void 0 ? void 0 : user.token) === null || _a === void 0 ? void 0 : _a.access_token) {
      var observeTokenData = user.token;
      tokenDataRef && (tokenDataRef.current = observeTokenData);
      onTokenChange === null || onTokenChange === void 0 ? void 0 : onTokenChange(observeTokenData);
      onStatusChange === null || onStatusChange === void 0 ? void 0 : onStatusChange(authStatus.done);
    }
    setStorage(authStateKey, authStates.authorized);
    Logger.deprecate('user.subscribed & user.isTrialUsed are deprecated properties of user in AuthContext. Please consider using user.subscription object for accessing these props.');
    var subscribed = (_b = user.subscription) === null || _b === void 0 ? void 0 : _b.granted;
    var isTrialUsed = (_c = user.subscription) === null || _c === void 0 ? void 0 : _c.is_trial_used;
    onUserChange === null || onUserChange === void 0 ? void 0 : onUserChange(_assign(_assign({}, user), {
      subscribed: subscribed,
      isTrialUsed: isTrialUsed
    }));
    addRecentLoginMethod({
      id: user.id,
      username: user.username,
      email: user.email,
      provider: provider
    });
    onStatusChange === null || onStatusChange === void 0 ? void 0 : onStatusChange(authStatus.resolved);
    authSuccessHandler === null || authSuccessHandler === void 0 ? void 0 : authSuccessHandler(user);
  }, [authSuccessHandler, onStatusChange, onTokenChange, onUserChange, tokenDataRef]);
  var finalizeAuthWithStagedUser = useCallback(function (username) {
    if (stagedUserInfo) {
      finalizeAuth(stagedUserInfo.provider, username ? _assign(_assign({}, stagedUserInfo.user), {
        username: username
      }) : stagedUserInfo.user);
      setStagedUserInfo(null);
    }
  }, [finalizeAuth, setStagedUserInfo, stagedUserInfo]);
  var _checkUser = useCallback(function (provider, user, removalInfo, action) {
    var _a, _b;
    if (user) {
      if ((_a = user.subscription) === null || _a === void 0 ? void 0 : _a.granted) {
        setCookie(IS_SUBSCRIBED_COOKIE_NAME, 'true');
      }
      if (user.id) {
        setCookie(GT_USER_ID_COOKIE_NAME, "".concat(user.id));
      }
      (_b = window === null || window === void 0 ? void 0 : window.pulse) === null || _b === void 0 ? void 0 : _b.call(window, 'state', {
        user_id: (user === null || user === void 0 ? void 0 : user.id) || null
      });
      var showUsernameForm = action === AuthActionOptions.SignUp ? showUsernameFormOnSignUp : showUsernameFormOnSignIn;
      if (showUsernameForm && provider === ProviderOptions.email) {
        setStagedUserInfo({
          provider: provider,
          user: user
        });
        usernameSuggestion.setUserData(user.email, UserField.email);
        setCurrentPage(ComponentsOptions.UsernameForm);
      } else {
        finalizeAuth(provider, user);
      }
    }
    if (removalInfo) {
      handleTerminatedAccount(removalInfo);
    }
  }, [showUsernameFormOnSignUp, showUsernameFormOnSignIn, setStagedUserInfo, setCurrentPage, finalizeAuth, handleTerminatedAccount]);
  var handleGeneralSocialServices = useCallback(function (_a) {
    var provider = _a.provider,
      credential = _a.credential,
      username = _a.username;
    handleNewSocialRequest({
      flow: flow,
      credential: credential,
      provider: provider,
      username: username,
      checkUser: function checkUser(user, removalInfo) {
        return _checkUser(provider, user, removalInfo);
      },
      triggeredFrom: triggeredFrom,
      setSocialData: setSocialData,
      setCurrentPage: setCurrentPage,
      setUsername: setUsername,
      checkedStates: checkedStates,
      isAuthCombined: isAuthCombined,
      checkBiometricState: showUsernameFormOnSignUp
    })["catch"](console.error);
  }, [_checkUser, checkedStates, flow, isAuthCombined, setCurrentPage, setSocialData, setUsername, showUsernameFormOnSignUp, triggeredFrom]);
  var handleAppleService = useCallback(function (data, fromWelcomeBack) {
    if ('authorization' in data) {
      fireSocialButtonClickAnalytics(AnalyticsSocialItemOptions.Apple);
      handleAppleRequest({
        flow: flow,
        appleData: data,
        checkUser: function checkUser(user, removalInfo) {
          return _checkUser(SocialProviderOptions.apple, user, removalInfo);
        },
        triggeredFrom: triggeredFrom,
        setCurrentPage: setCurrentPage,
        setUsername: setUsername,
        checkedStates: checkedStates,
        setSocialData: setSocialData,
        isAuthCombined: isAuthCombined,
        fromWelcomeBack: fromWelcomeBack,
        checkBiometricState: showUsernameFormOnSignUp
      })["catch"](console.error);
    }
  }, [flow, triggeredFrom, setCurrentPage, setUsername, checkedStates, setSocialData, isAuthCombined, showUsernameFormOnSignUp, _checkUser]);
  var handleGoogleService = useCallback(function (data, fromWelcomeBack, onClick) {
    return __awaiter(_this, void 0, void 0, function () {
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            fireSocialButtonClickAnalytics(AnalyticsSocialItemOptions.Google);
            return [4, handleGoogleResponse({
              flow: flow,
              accountData: data,
              checkUser: function checkUser(user, removalInfo) {
                return _checkUser(SocialProviderOptions.google, user, removalInfo);
              },
              triggeredFrom: triggeredFrom,
              setCurrentPage: setCurrentPage,
              checkedStates: checkedStates,
              onClick: onClick,
              setSocialData: setSocialData,
              setUsername: setUsername,
              isAuthCombined: isAuthCombined,
              fromWelcomeBack: fromWelcomeBack,
              checkBiometricState: showUsernameFormOnSignUp
            })["catch"](console.error)];
          case 1:
            _a.sent();
            return [2];
        }
      });
    });
  }, [flow, triggeredFrom, setCurrentPage, checkedStates, setSocialData, setUsername, isAuthCombined, showUsernameFormOnSignUp, _checkUser]);
  var handleFacebookService = useCallback(function (fromWelcomeBack) {
    fireSocialButtonClickAnalytics(AnalyticsSocialItemOptions.Facebook);
    handleFacebookResponse({
      flow: flow,
      checkUser: function checkUser(user, removalInfo) {
        return _checkUser(SocialProviderOptions.facebook, user, removalInfo);
      },
      triggeredFrom: triggeredFrom,
      setCurrentPage: setCurrentPage,
      checkedStates: checkedStates,
      setSocialData: setSocialData,
      setUsername: setUsername,
      isAuthCombined: isAuthCombined,
      fromWelcomeBack: fromWelcomeBack,
      checkBiometricState: showUsernameFormOnSignUp
    });
  }, [flow, triggeredFrom, setCurrentPage, checkedStates, setSocialData, setUsername, isAuthCombined, showUsernameFormOnSignUp, _checkUser]);
  var handleSocials = useCallback(function (socialName, userData) {
    if (userData === void 0) {
      userData = null;
    }
    return handleSocialSignIn({
      flow: flow,
      socialName: socialName,
      checkUser: function checkUser(user, removalInfo) {
        return _checkUser(socialName, user, removalInfo);
      },
      triggeredFrom: triggeredFrom,
      userData: userData,
      isAuthCombined: isAuthCombined
    });
  }, [_checkUser, flow, isAuthCombined, triggeredFrom]);
  var onSubmit = useCallback(function (_a) {
    var email = _a.email,
      action = _a.action,
      password = _a.password,
      code = _a.code,
      username = _a.username,
      checkedStates = _a.checkedStates,
      fromWelcomeBack = _a.fromWelcomeBack,
      externalProvider = _a.externalProvider;
    return handleSubmit({
      flow: flow,
      email: email,
      username: username,
      password: password,
      code: code,
      preventReCaptcha: preventReCaptcha,
      externalProvider: externalProvider,
      checkUser: function checkUser(user, removalInfo) {
        return _checkUser(externalProvider !== null && externalProvider !== void 0 ? externalProvider : ProviderOptions.email, user, removalInfo, action);
      },
      onTerminatedAccount: handleTerminatedAccount,
      triggeredFrom: triggeredFrom,
      action: action,
      setServerStatus: setServerStatus,
      checkedStates: checkedStates,
      fromWelcomeBack: fromWelcomeBack,
      isAuthCombined: isAuthCombined,
      redirectAfterEmailConfirmation: redirectAfterEmailConfirmation,
      finalizeAuth: finalizeAuth,
      checkBiometricState: showUsernameFormOnSignUp
    });
  }, [flow, preventReCaptcha, handleTerminatedAccount, triggeredFrom, setServerStatus, isAuthCombined, redirectAfterEmailConfirmation, finalizeAuth, showUsernameFormOnSignUp, _checkUser]);
  var onEmailCodeSend = useCallback(function (email) {
    return emailCodeSendRequest({
      email: email,
      preventReCaptcha: preventReCaptcha
    });
  }, [preventReCaptcha]);
  return {
    onSubmit: onSubmit,
    handleSocials: handleSocials,
    handleTerminatedAccount: handleTerminatedAccount,
    handleAppleService: handleAppleService,
    handleGoogleService: handleGoogleService,
    handleFacebookService: handleFacebookService,
    handleGeneralSocialServices: handleGeneralSocialServices,
    finalizeAuthWithStagedUser: finalizeAuthWithStagedUser,
    checkUser: _checkUser,
    onEmailCodeSend: onEmailCodeSend,
    terminatedAccount: terminatedAccount,
    serverStatus: serverStatus
  };
}

function useGoogleAuth(handleGoogleResponse, buttonElement, buttonWidth, onClick, hideOneTap, disableGoogleInit, prefilledEmail) {
  var _a, _b;
  if (buttonElement === void 0) {
    buttonElement = null;
  }
  if (buttonWidth === void 0) {
    buttonWidth = null;
  }
  if (onClick === void 0) {
    onClick = null;
  }
  if (hideOneTap === void 0) {
    hideOneTap = false;
  }
  var _c = useAuthContext(),
    tokenData = _c.tokenData,
    status = _c.status;
  var _d = useState(false),
    isInitialised = _d[0],
    setInitialised = _d[1];
  var oneTapExcludedPaths = ['/about-us', '/press-kit', '/challenges', '/terms-of-use', '/privacy-policy', '/community-guidelines', '/copyright-dispute-policy', '/challenge/*'];
  var redirectUrl = IS_BROWSER && ((_a = window === null || window === void 0 ? void 0 : window.location) === null || _a === void 0 ? void 0 : _a.href);
  var pathName = IS_BROWSER && ((_b = window === null || window === void 0 ? void 0 : window.location) === null || _b === void 0 ? void 0 : _b.pathname);
  var excluded = oneTapExcludedPaths && oneTapExcludedPaths.length > 0 ? pathName && oneTapExcludedPaths.includes(pathName) : false;
  var isScriptLoaded = useDelayedScript(GOOGLE_PLATFORM_SCRIPT_URL, false);
  var googleResponseHandler = useCallback(function (accountData) {
    var _a;
    (_a = handleGoogleResponse(accountData, false, onClick)) === null || _a === void 0 ? void 0 : _a["catch"](console.error);
  }, [handleGoogleResponse, onClick]);
  useEffect(function () {
    var _a, _b, _c, _d;
    if (IS_BROWSER && (tokenData === null || tokenData === void 0 ? void 0 : tokenData.access_token)) {
      (_c = (_b = (_a = window === null || window === void 0 ? void 0 : window.google) === null || _a === void 0 ? void 0 : _a.accounts) === null || _b === void 0 ? void 0 : _b.id) === null || _c === void 0 ? void 0 : _c.cancel();
    } else if (IS_BROWSER && isScriptLoaded && !isInitialised && ((_d = window === null || window === void 0 ? void 0 : window.google) === null || _d === void 0 ? void 0 : _d.accounts) && !disableGoogleInit) {
      var config = {
        redirectUrl: redirectUrl,
        googleAccountConfigs: {
          client_id: GOOGLE_CLIENT_ID,
          login_hint: prefilledEmail
        },
        paths: ['/sign-in', '/sign-up']
      };
      window.google.accounts.id.initialize(_assign(_assign({}, config.googleAccountConfigs), {
        callback: function callback(accountData) {
          return googleResponseHandler(accountData);
        }
      }));
      setInitialised(true);
    }
  }, [googleResponseHandler, redirectUrl, isInitialised, isScriptLoaded, tokenData === null || tokenData === void 0 ? void 0 : tokenData.access_token, disableGoogleInit, prefilledEmail]);
  useLayoutEffect(function () {
    var _a;
    if (IS_BROWSER && isScriptLoaded && isInitialised && buttonElement && ((_a = window === null || window === void 0 ? void 0 : window.google) === null || _a === void 0 ? void 0 : _a.accounts)) {
      var buttonRenderOptions = {
        theme: googleButtonTheme.outline,
        size: googleButtonSize.large,
        text: googleButtonLabelText.continue_with,
        shape: googleButtonShape.pill,
        width: buttonWidth !== null && buttonWidth !== void 0 ? buttonWidth : buttonElement.getBoundingClientRect().width
      };
      window.google.accounts.id.renderButton(buttonElement, buttonRenderOptions);
    }
  }, [buttonElement, buttonWidth, isInitialised, isScriptLoaded]);
  useEffect(function () {
  }, [excluded, hideOneTap, isInitialised, isScriptLoaded, pathName, status]);
}

export { useGoogleAuth as a, useAuth as u };
//# sourceMappingURL=useGoogleAuth-d54a1d14.js.map
